/**
 * SparklineChart Component
 * 
 * Lightweight sparkline chart using SVG. No external dependencies.
 * Displays price trend for a ticker over a specified period.
 * Supports hover tooltips and buy time indicators.
 */

import { useMemo, useState, useRef } from 'react';
import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import type { TickerHistoryDataPoint } from '../types';

export type SparklinePeriod = '1H' | '12H' | '1D' | '7D' | '1M' | '1Y';

interface SparklineChartProps {
  data: TickerHistoryDataPoint[];
  period: SparklinePeriod; // Used for tooltip/context, even if not directly in rendering
  height?: number;
  width?: number;
  color?: 'up' | 'down' | 'neutral';
  isLoading?: boolean;
  purchaseDate?: string; // YYYY-MM-DD format for buy time indicator
}

export function SparklineChart({
  data,
  period: _period,
  height = 40,
  width = 120,
  color = 'neutral',
  isLoading = false,
  purchaseDate,
}: SparklineChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { path, isPositive, points, buyTimeX } = useMemo(() => {
    if (!data || data.length === 0) {
      return { path: '', isPositive: true, points: [], minPrice: 0, maxPrice: 0, buyTimeX: null };
    }

    // Extract close prices with dates
    const pricesWithDates = data
      .map((d, idx) => ({ price: d.close, date: d.date, index: idx }))
      .filter((d) => d.price > 0);
    
    if (pricesWithDates.length === 0) {
      return { path: '', isPositive: true, points: [], minPrice: 0, maxPrice: 0, buyTimeX: null };
    }

    const prices = pricesWithDates.map((d) => d.price);
    const dates = pricesWithDates.map((d) => d.date);

    // Determine if trend is positive
    const firstPrice = prices[0];
    const lastPrice = prices[prices.length - 1];
    const isPositive = lastPrice >= firstPrice;

    // Calculate min/max for scaling
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice || 1; // Avoid division by zero

    // Padding for visual clarity
    const padding = 2;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Generate points with dates
    const points = pricesWithDates.map((item, index) => {
      const x = padding + (index / (prices.length - 1 || 1)) * chartWidth;
      const y =
        padding +
        chartHeight -
        ((item.price - minPrice) / priceRange) * chartHeight;
      return { x, y, price: item.price, date: item.date, index };
    });

    // Calculate buy time indicator position if purchase date is in range
    let buyTimeX: number | null = null;
    if (purchaseDate && dates.length > 0) {
      const firstDate = dates[0];
      const lastDate = dates[dates.length - 1];
      if (purchaseDate >= firstDate && purchaseDate <= lastDate) {
        // Find the closest data point to purchase date
        const purchaseDateObj = new Date(purchaseDate);
        let closestIndex = 0;
        let minDiff = Infinity;
        dates.forEach((date, idx) => {
          const dateObj = new Date(date);
          const diff = Math.abs(purchaseDateObj.getTime() - dateObj.getTime());
          if (diff < minDiff) {
            minDiff = diff;
            closestIndex = idx;
          }
        });
        if (points[closestIndex]) {
          buyTimeX = points[closestIndex].x;
        }
      }
    }

    // Create SVG path string
    if (points.length === 0) {
      return { path: '', isPositive, points: [], minPrice, maxPrice, buyTimeX };
    }

    const pathString = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');

    return { path: pathString, isPositive, points, buyTimeX };
  }, [data, width, height, purchaseDate]);

  // Determine color based on trend
  const strokeColor =
    color === 'up'
      ? '#4caf50' // Green
      : color === 'down'
      ? '#f44336' // Red
      : isPositive
      ? '#4caf50'
      : '#f44336';

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || points.length === 0) return;
    
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    
    // Find closest point
    let closestIndex = 0;
    let minDistance = Infinity;
    points.forEach((point, idx) => {
      const distance = Math.abs(point.x - x);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = idx;
      }
    });
    
    setHoveredIndex(closestIndex);
    setHoverPosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    setHoverPosition(null);
  };

  const formatPrice = (price: number): string => {
    return `$${price.toFixed(2)}`;
  };

  const formatDate = (date: string): string => {
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return date;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ width, height, display: 'flex', alignItems: 'center' }}>
        <Skeleton variant="rectangular" width={width} height={height} />
      </Box>
    );
  }

  if (!data || data.length === 0 || !path) {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          fontSize: '0.75rem',
        }}
      >
        No data
      </Box>
    );
  }

  const hoveredPoint = hoveredIndex !== null ? points[hoveredIndex] : null;

  return (
    <Box sx={{ width, height, display: 'flex', alignItems: 'center', position: 'relative' }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ overflow: 'visible', cursor: 'crosshair' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Main price line */}
        <path
          d={path}
          fill="none"
          stroke={strokeColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Buy time indicator line */}
        {buyTimeX !== null && (
          <line
            x1={buyTimeX}
            y1={0}
            x2={buyTimeX}
            y2={height}
            stroke="#ff9800"
            strokeWidth="1"
            strokeDasharray="2,2"
            opacity={0.7}
          />
        )}
        
        {/* Hover indicator circle */}
        {hoveredPoint && (
          <>
            <circle
              cx={hoveredPoint.x}
              cy={hoveredPoint.y}
              r="3"
              fill={strokeColor}
              stroke="white"
              strokeWidth="1"
            />
            {/* Invisible hit area for better hover detection */}
            <rect
              x={hoveredPoint.x - 5}
              y={0}
              width={10}
              height={height}
              fill="transparent"
            />
          </>
        )}
      </svg>
      
      {/* Hover tooltip */}
      {hoveredPoint && hoverPosition && (
        <Tooltip
          open={true}
          title={
            <Box>
              <Typography variant="caption" display="block">
                {formatDate(hoveredPoint.date)}
              </Typography>
              <Typography variant="caption" display="block" fontWeight="bold">
                {formatPrice(hoveredPoint.price)}
              </Typography>
            </Box>
          }
          arrow
          placement="top"
          PopperProps={{
            anchorEl: {
              getBoundingClientRect: () => ({
                width: 0,
                height: 0,
                top: hoverPosition.y,
                left: hoverPosition.x,
                right: hoverPosition.x,
                bottom: hoverPosition.y,
                x: hoverPosition.x,
                y: hoverPosition.y,
                toJSON: () => {},
              }),
            },
          }}
        >
          <Box />
        </Tooltip>
      )}
    </Box>
  );
}

